import React, { useEffect, useState } from "react";
import { DashboardPreviewHeader } from "../DashboardPreviewHeader";
import StoreInput from "@/components/StoreComponents/StoreInput";
import {
  ButtonLoadingEllipsis,
  OutlineButton,
  TrackedMotionButton,
} from "../../../../StoreComponents/StoreButton";
import { styled } from "@mui/material/styles";
import StoreToggle from "@/components/StoreComponents/StoreToggle";
import { getUserBrandAppConfigInit, updateUserBrandAppConfig } from "@/api";
import { useAppSelector } from "@/redux/hooks";

const NewsletterContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  ".newsletter_toggle": {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: "8px",
    ".newsletter_toggle_info": {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      paddingTop: "3px",
    },
    ".newsletter_toggle_info_title": {
      fontSize: 12,
      fontWeight: 500,
    },
    ".newsletter_toggle_info_description": {
      fontSize: 10,
      color: theme.vars.palette.text.secondary,
      a: {
        color: theme.vars.palette.text.secondary,
        textDecoration: "underline",
      },
    },
  },
  ".newsletter_action": {
    marginTop: 42,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);

  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(true);
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const brandAppConfig = useAppSelector(
    ({ appConfig }) => appConfig?.brandAppConfig
  );

  const policyUrl = brandAppConfig?.modulesConfig?.newsletterModule?.policyUrl;

  useEffect(() => {
    (async () => {
      try {
        const data = await getUserBrandAppConfigInit();

        setSubscribeToNewsletter(
          data?.extendedAttributes?.newsletterSubscribed
        );
        setEmail(
          data?.extendedAttributes?.newsletterEmail || userProfile?.email
        );
        // Optionally prefill local UI from init config if needed
        // Example:
        // setSubscribeToNewsletter(!!data?.subscribeToNewsletterDefault);
      } catch (e) {
        console.error("Failed to fetch user brand app config init:", e);
      }
    })();
  }, []);

  const handleUpdateNewsletter = async () => {
    try {
      setSaving(true);
      setSaveButtonDisabled(true);
      const data = await updateUserBrandAppConfig({
        config: {
          newsletterSubscribed: subscribeToNewsletter,
          newsletterEmail: email,
        },
      });
      setSaved(true);
      setSaving(false);
      setSaveButtonDisabled(false);
    } catch (e) {
      console.error("Failed to update user brand app config:", e);
    }
  };

  const cancelClick = () => {
    setEmail(userProfile?.email || "");
    setSubscribeToNewsletter(true);
    setSaveButtonDisabled(false);
  };

  return (
    <NewsletterContainer>
      <DashboardPreviewHeader
        title={"Newsletter"}
        subtitle={"Sign up for exclusive discounts, offers, hints and tips"}
        sx={{ ".subtitle": { fontSize: 18 } }}
        marginBottom={10}
      />
      <div>
        <StoreInput
          sx={{ maxWidth: "449px" }}
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="newsletter_toggle">
        <StoreToggle
          checked={subscribeToNewsletter}
          onChange={(e) => setSubscribeToNewsletter(e.target.checked)}
        />
        <div className="newsletter_toggle_info">
          <div className="newsletter_toggle_info_title">
            Send my news and offers from {brandAppConfig?.appName} to my email
          </div>
          {policyUrl ? (
            <div className="newsletter_toggle_info_description">
              You can unsubscribe at any time - please see{" "}
              <a href={policyUrl} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              .
            </div>
          ) : null}
        </div>
      </div>
      <div className="newsletter_action">
        <OutlineButton
          sx={{ minWidth: "83px" }}
          disabled={saving}
          onClick={cancelClick}
        >
          Cancel
        </OutlineButton>
        <div style={{ flex: 1 }}></div>
        <TrackedMotionButton
          color="primary"
          sx={{ minWidth: "83px" }}
          variant={saveButtonDisabled ? "outline" : "solid"}
          disabled={saveButtonDisabled}
          onClick={handleUpdateNewsletter}
        >
          {saved ? (
            "Saved!"
          ) : saving ? (
            <>
              Saving
              <ButtonLoadingEllipsis baseFontSize={12} />
            </>
          ) : (
            "Save"
          )}
        </TrackedMotionButton>
      </div>
    </NewsletterContainer>
  );
};

export default Newsletter;
